import React from 'react';

import type { ChatV2QuickActionCategory } from './types';

import { ReactComponent as CalendarBlankOutline } from '../../../assets/chat/calendar_blank.svg';
import { ReactComponent as Comment } from '../../../assets/chat/comment.svg';
import { ReactComponent as Plus } from '../../../assets/chat/plus_circle.svg';
import { ReactComponent as ActionTask } from '../../../assets/chat/action_task.svg';
import { ReactComponent as ActionEntireDay } from '../../../assets/chat/action_entire_day.svg';
import { ReactComponent as ActionWeek } from '../../../assets/chat/action_week.svg';
import { ReactComponent as ActionSleep } from '../../../assets/chat/action_sleep.svg';
import { ReactComponent as ActionWorkout } from '../../../assets/chat/action_workout.svg';
import { ReactComponent as EnergyBoostIcon } from '../../../assets/calendar/energyBoost.svg';
import { ReactComponent as CommentMulti } from '../../../assets/chat/comment_multi.svg';
import { ReactComponent as Books } from '../../../assets/chat/books.svg';
import { BugReportOutlined } from '@mui/icons-material';
import { getCssVariable, isDevelopment } from '../../../utils';

const primaryColor = getCssVariable('--color-primary');
const peachLightColor = getCssVariable('--color-peach-light');
const oliveDarkColor = getCssVariable('--color-olive-dark');
const grayDarkColor = getCssVariable('--color-gray-dark');
const yellowBaseColor = getCssVariable('--color-yellow-base');
const purpleTaroColor = getCssVariable('--color-purple-taro');
const whiteColor = getCssVariable('--color-white-lightest');

export const chatV2QuickActionCategories: ChatV2QuickActionCategory[] = [
  {
    id: 'plan-my',
    title: 'Plan my',
    icon: <CalendarBlankOutline style={{ color: whiteColor }} />,
    accentColor: primaryColor,
    items: [
      {
        id: 'plan-my-entire-day',
        label: 'Entire day',
        icon: <ActionEntireDay style={{ color: '#C9B8FF' }} />,
        planType: 'scheduler',
        prompt: '📅 Plan my entire day',
      },
      {
        id: 'plan-my-week',
        label: 'Week',
        badgeLabel: 'beta',
        icon: <ActionWeek />,
        planType: 'scheduler',
        prompt: '📅 Plan my week',
      },
      {
        id: 'plan-my-tasks',
        label: 'Tasks',
        icon: <ActionTask />,
        planType: 'task',
        prompt: 'Plan my tasks today',
      },
      {
        id: 'plan-my-workout',
        label: 'Workout',
        icon: <ActionWorkout />,
        prompt: 'Plan my workout today',
      },
      {
        id: 'plan-my-custom',
        label: 'Anything',
        icon: <Books style={{ color: grayDarkColor }} />,
        prefillText: 'Plan my ',
        isCustom: true,
      },
    ],
  },
  {
    id: 'tell-me',
    title: 'Tell me',
    detailTitle: 'Tell me about...',
    icon: <Comment />,
    accentColor: peachLightColor,
    items: [
      {
        id: 'tell-me-what-to-do',
        label: 'What to do now',
        icon: <CommentMulti height={14} width={14} style={{ color: purpleTaroColor }} />,
        autoFocus: true,
        prompt: 'Tell me about what to do now',
      },
      {
        id: 'tell-me-tasks',
        label: 'Tasks',
        icon: <ActionTask />,
        autoFocus: true,
        prompt: 'Tell me about my tasks today',
      },
      {
        id: 'tell-me-schedule',
        label: 'Schedule',
        icon: <CalendarBlankOutline style={{ color: yellowBaseColor }} />,
        autoFocus: true,
        prompt: 'Tell me about my schedule today',
      },
      {
        id: 'tell-me-sleep',
        label: 'Sleep',
        icon: <ActionSleep />,
        autoFocus: true,
        prompt: 'Tell me about my sleep last night',
      },
      {
        id: 'tell-me-energy',
        label: 'Energy',
        icon: <EnergyBoostIcon style={{ color: yellowBaseColor }} width={14} height={14} />,
        autoFocus: true,
        prompt: 'Tell me about my energy today',
      },
      {
        id: 'tell-me-custom',
        label: 'Anything',
        icon: <Books style={{ color: grayDarkColor }} />,
        prefillText: 'Tell me about my ',
        isCustom: true,
      },
    ],
  },
  {
    id: 'more',
    title: 'More',
    icon: <Plus />,
    accentColor: oliveDarkColor,
    items: [
      {
        id: 'more-decompress-time',
        label: 'Add time to decompress after my meetings',
        prompt: 'Add time to decompress after my meetings',
      },
      {
        id: 'more-block-focus-time',
        label: 'Block focus time',
        prompt: 'Block focus time',
      },
      {
        id: 'more-recharge-events',
        label: 'Add events to recharge and recover',
        prompt: 'Add events to recharge and recover',
      },
      {
        id: 'more-break-down-tasks',
        label: 'Break down my tasks into smaller ones',
        prompt: 'Break down my tasks into smaller ones and create them',
      },
      {
        id: 'more-rate-schedule',
        label: 'Rate my schedule today',
        prompt: 'Rate my schedule today',
      },
      {
        id: 'more-meeting-prep',
        label: 'What should I do to prepare for my next meeting',
        prompt: 'What should I do to prepare for my next meeting',
      },
      // Dev-only: injects a canned conversation (rich-context cards, or a
      // "Replan" result_json) bypassing the network, so the v4 suggestion
      // UI can be visually verified without a live AI backend. Never shown
      // outside development builds — see `isDevelopment` guard.
      ...(isDevelopment
        ? [
            {
              id: 'debug-mock-rich-context',
              label: 'Mock chat w/ rich context',
              icon: <BugReportOutlined />,
            },
            {
              id: 'debug-mock-replan',
              label: 'Mock chat w/ replan result',
              icon: <BugReportOutlined />,
            },
            {
              id: 'debug-mock-plan-status',
              label: 'Mock plan status messages',
              icon: <BugReportOutlined />,
            },
          ]
        : []),
    ],
  },
];
